// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateRangeInput } from '@/components/date-range-input';
import { TooltipTypography } from '@/components/tooltip-typography';
import {
  DATE_RANGE_SHORTCUT_LAST_30_DAYS,
  DATE_RANGE_SHORTCUT_LAST_60_DAYS,
  DATE_RANGE_SHORTCUT_LAST_90_DAYS,
  DATE_RANGE_SHORTCUT_LAST_FULL_MONTH,
  DATE_RANGE_SHORTCUT_LAST_MONTH_TO_DATE,
  DATE_RANGE_SHORTCUT_THIS_MONTH,
  DateRangeShortcut,
} from '@/constants/date-range-shortcut';
import { Stack, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { DateTime, IANAZone, Interval } from 'luxon';
import { PropsWithChildren } from 'react';

const DATE_RANGE_SHORTCUTS: DateRangeShortcut[] = [
  DATE_RANGE_SHORTCUT_THIS_MONTH,
  DATE_RANGE_SHORTCUT_LAST_MONTH_TO_DATE,
  DATE_RANGE_SHORTCUT_LAST_FULL_MONTH,
  DATE_RANGE_SHORTCUT_LAST_30_DAYS,
  DATE_RANGE_SHORTCUT_LAST_60_DAYS,
  DATE_RANGE_SHORTCUT_LAST_90_DAYS,
];

interface Props {
  interval: Interval;
  timezone: IANAZone;
  onChange: (dateRange: DateRange<DateTime>) => void;
  error?: boolean;
  helperText?: string;
  /** When false, shows empty date inputs instead of the interval values */
  hasDateRange?: boolean;
  label?: string;
  shortcuts?: DateRangeShortcut[];
  /** Hides the "Reported for {timezone}" caption above the date range. */
  hideReportedFor?: boolean;
}

export function ReportDateRangeInput(props: PropsWithChildren<Props>) {
  const hasDateRange = props.hasDateRange ?? true;
  const dateRange: DateRange<DateTime> = hasDateRange
    ? [props.interval.start, props.interval.end]
    : [null, null];

  return (
    <Stack spacing={2} alignItems="end">
      {!props.hideReportedFor && (
        <Typography variant="caption" color="text.secondary">
          Reported for{' '}
          <TooltipTypography
            variant="inherit"
            component="span"
            tooltip="Determined based on your organization's time zone setting"
          >
            {props.timezone.name}
          </TooltipTypography>
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        {props.children}
        <DateRangeInput
          width="200px"
          label={props.label ?? 'Date range'}
          value={dateRange}
          onChange={props.onChange}
          shortcuts={props.shortcuts ?? DATE_RANGE_SHORTCUTS}
          error={props.error}
          helperText={props.helperText}
        />
      </Stack>
    </Stack>
  );
}
